import { getThemeColors } from "../lib/theme";
import { formatDurationCompact } from "../lib/utils/time";
import { useTranslation } from "react-i18next";
import { ProviderBadge } from "./ProviderBadge";
import { ProfileAvatar } from "./ProfileAvatar";

export interface LeaderboardEntry {
  name: string;
  /** Provider of the player's most recent sign-in, rendered as the badge. */
  provider?: string | null;
  image?: string | null;
  time: number;
  clicks: number;
  meta?: string; // Additional info like "3/5" for found/target count
  isCurrentUser?: boolean;
}

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  emptyMessage?: string;
  maxItems?: number;
  showDividers?: boolean;
  rounded?: boolean;
}

export function Leaderboard({
  entries,
  isDark,
  theme,
  emptyMessage,
  maxItems = 20,
  showDividers = true,
  rounded = false,
}: LeaderboardProps) {
  const { t } = useTranslation();
  const displayEntries = entries.slice(0, maxItems);

  if (displayEntries.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className={theme.textMuted}>
          {emptyMessage ?? t("components.leaderboard.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {displayEntries.map((entry, index) => (
        <LeaderboardRow
          key={entry.name + index}
          entry={entry}
          index={index}
          isDark={isDark}
          theme={theme}
          showDivider={showDividers && index !== 0}
          rounded={rounded}
        />
      ))}
    </div>
  );
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  index: number;
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  showDivider?: boolean;
  rounded?: boolean;
}

export function LeaderboardRow({
  entry,
  index,
  isDark,
  theme,
  showDivider = true,
  rounded = false,
}: LeaderboardRowProps) {
  const getRankStyle = () => {
    if (index === 0) return "text-yellow-400";
    if (index === 1) return isDark ? "text-zinc-300" : "text-gray-500";
    if (index === 2) return "text-orange-400";
    return theme.textMuted;
  };

  const rowClasses = rounded
    ? `flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 ${
        entry.isCurrentUser
          ? isDark
            ? "bg-zinc-800 border-zinc-600"
            : "bg-gray-100 border-gray-400"
          : isDark
            ? "bg-zinc-950/30 border-zinc-800/50 hover:border-zinc-700/50"
            : "bg-white/50 border-gray-200/50 hover:border-gray-300/50"
      }`
    : `flex items-center gap-4 p-4 transition-all duration-200 ${
        showDivider ? `border-t ${theme.border}` : ""
      } hover:bg-opacity-50 ${isDark ? "hover:bg-zinc-800/50" : "hover:bg-gray-50/50"}`;

  return (
    <div className={rowClasses}>
      <div className={`w-8 text-center font-bold font-mono ${getRankStyle()}`}>
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ProfileAvatar image={entry.image} name={entry.name} size="sm" />
        <div
          className={`truncate ${entry.isCurrentUser ? "font-semibold" : ""} ${theme.text}`}
        >
          {entry.name}
        </div>
        <ProviderBadge provider={entry.provider} />
      </div>

      <div className={`text-right tabular-nums font-mono ${theme.textMuted}`}>
        {formatDurationCompact(entry.time)}
      </div>

      <div
        className={`text-right w-16 font-mono text-sm ${theme.textSecondary}`}
      >
        {entry.meta || entry.clicks}
      </div>
    </div>
  );
}
